import {
	HttpInterceptorFn,
	HttpRequest,
	HttpHandlerFn,
	HttpEvent,
	HttpErrorResponse,
} from '@angular/common/http';
import { inject } from '@angular/core';
import {
	Observable,
	throwError,
	of,
	switchMap,
	catchError,
	map,
	tap,
	from,
	timer,
} from 'rxjs';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Router } from '@angular/router';
import { NotificationService } from '../notification-service/notification.service';
import { AuthTokenService } from '../services/auth-token.service';
import { DynamicModalComponent } from '@meta-press/shared-plugins';
import { LoginFormComponent } from '../components/login-form/login-form.component';
import { environment } from '../../../environments/environment';
import { LoginResponse, ErrorResponse } from '@meta-press/shared-plugins';
import { HttpClient } from '@angular/common/http';
import { LoginService } from '../login-service/login.service';

// Auth Interceptor intercepts all HTTP requests and adds authentication tokens. Currently it doesn't have separation between services and assumes we are making Azure calls. OpenAI-calls are not currently intercepted, because they are made with via openai-client. Currently openai-client has access token in its header and we simply reinitialize it. It would be ideal that all calls go through this service.

// Note: Timer state is now managed by AuthTokenService

// *** TEMPORARY TEST CODE FOR RE-LOGIN MODAL TESTING ***
// This code forces the re-login modal to appear 15 seconds after login
//
// TO DISABLE THIS TEST:
// 1. Set ENABLE_RELOGIN_TEST = false, OR
// 2. Remove all blocks marked with "*** TEMPORARY TEST CODE ***"
//
// This test works by:
// - Recording timestamp when user first logs in
// - Forcing token "expiration" after 15 seconds
// - Triggering the re-login modal
// - Resetting timestamp after successful re-login
const ENABLE_RELOGIN_TEST = false;
const RELOGIN_TEST_DELAY_MS = 15 * 1000; // 15 seconds
let loginTimestamp: number | null = null;
// *** END TEST CODE ***

// Global state to prevent multiple re-login modals
let isReLoginModalOpen = false;
let reLoginPromise: Promise<boolean> | null = null;

// Retry configuration
const MAX_RETRY_ATTEMPTS = 4;
const RETRY_DELAY_BASE_MS = 3000; // 3 second delay

/**
 * Retry an operation with exponential backoff
 */
function withRetry<T>(maxRetries: number = MAX_RETRY_ATTEMPTS) {
	return (source: Observable<T>) => {
		let retryCount = 0;

		return source.pipe(
			tap((result) => {
				// Log successful retry if we had previous failures
				if (retryCount > 0) {
					console.log(`Auth operation succeeded after ${retryCount} retry attempt(s)`);
				}
			}),
			catchError((error) => {
				// Only retry on specific errors (network issues, timeouts, 5xx server errors)
				const shouldRetry =
					error?.status >= 500 ||
					error?.status === 0 ||
					error?.name === 'TimeoutError' ||
					error?.name === 'HttpTimeoutError' ||
					error?.message?.includes('Network') ||
					error?.message?.includes('timeout') ||
					error?.message?.includes('Timeout') ||
					error?.message?.includes('ETIMEDOUT') ||
					error?.message?.includes('Request timeout') ||
					error?.code === 'ETIMEDOUT' ||
					error?.type === 'timeout';

				if (!shouldRetry || retryCount >= maxRetries) {
					if (retryCount >= maxRetries) {
						console.log(
							`Max retry attempts (${maxRetries}) reached for auth operation`,
						);
					}
					return throwError(() => error);
				}

				retryCount++;
				const delay = RETRY_DELAY_BASE_MS;
				console.log(
					`Auth operation failed, retrying in ${delay}ms (attempt ${retryCount}/${maxRetries})`,
					'Error details:', 
					{
						status: error?.status,
						statusText: error?.statusText,
						message: error?.message,
						name: error?.name,
						type: error?.type,
						code: error?.code
					}
				);

				return timer(delay).pipe(switchMap(() => source));
			}),
		);
	};
}

/**
 * Functional auth interceptor for Angular standalone applications
 */
export const authInterceptor: HttpInterceptorFn = (
	req: HttpRequest<any>,
	next: HttpHandlerFn,
): Observable<HttpEvent<any>> => {
	// Inject services
	const authTokenService = inject(AuthTokenService);
	const modalService = inject(NgbModal);
	const router = inject(Router);
	const notificationService = inject(NotificationService);
	const http = inject(HttpClient);

	// Skip authentication for login and refresh endpoints
	if (isAuthEndpoint(req.url)) {
		return next(req);
	}

	// Only add authentication to requests that need it
	if (needsAuthentication(req)) {
		// Special handling for login in progress state
		if (
			!authTokenService.hasTokens() &&
			authTokenService.isLoginInProgress()
		) {
			console.log(
				'No tokens available but login is in progress - checking if request should be allowed',
			);

			// During login process, allow certain types of requests to proceed:
			// - Static assets (images, CSS, JS files)
			// - Public resources that don't require authentication
			if (shouldAllowDuringLogin(req)) {
				console.log('Allowing request during login:', req.url);
				return next(req);
			} else {
				console.log(
					'Blocking API request during login - will retry after login completes:',
					req.url,
				);
				return throwError(
					() =>
						new Error(
							'Login in progress - authentication not yet available',
						),
				);
			}
		}

		return ensureValidToken(
			authTokenService,
			modalService,
			router,
			notificationService,
			http,
			req,
			next,
		).pipe(
			switchMap((token) => {
				const authReq = req.clone({
					setHeaders: {
						Authorization: `Bearer ${token}`,
					},
				});
				return next(authReq).pipe(withRetry(MAX_RETRY_ATTEMPTS));
			}),
			catchError((error) => {
				// Handle authentication errors
				if (
					error instanceof HttpErrorResponse &&
					error.status === 401
				) {
					// Check if this might be related to test mode
					const isLikelyTestMode =
						ENABLE_RELOGIN_TEST && loginTimestamp !== null;
					if (isLikelyTestMode) {
						console.log(
							'🧪 401 ERROR in TEST MODE: This may be related to test simulation',
							error.url,
						);
					} else {
						console.log(
							'🚨 REAL 401 ERROR: Token validation failed on server',
							error.url,
						);
					}

					// Token might be invalid, try re-authentication
					return handleUnauthorized(
						req,
						next,
						authTokenService,
						modalService,
						router,
						notificationService,
						http,
					);
				}
				return throwError(() => error);
			}),
		);
	}

	return next(req);
};

/**
 * Ensures access token is available and valid with enhanced four-state logic
 */
function ensureValidToken(
	authTokenService: AuthTokenService,
	modalService: NgbModal,
	router: Router,
	notificationService: NotificationService,
	http: HttpClient,
	req: HttpRequest<any>,
	next: HttpHandlerFn,
): Observable<string> {
	const now = Date.now();
	const refreshIntervalMillis =
		environment.authTokenRefreshIntervalMinutes * 60 * 1000;

	// *** TEMPORARY TEST CODE - Track login timestamp ***
	if (
		ENABLE_RELOGIN_TEST &&
		authTokenService.hasTokens() &&
		loginTimestamp === null
	) {
		loginTimestamp = Date.now();
		console.log(
			'🧪 TEST: Login timestamp recorded:',
			new Date(loginTimestamp).toISOString(),
		);
	}
	// *** END TEST CODE ***

	// State 1: No token at all
	if (!authTokenService.hasTokens()) {
		console.log('No tokens available - logging out user');
		// Reset test timestamp when tokens are cleared
		if (ENABLE_RELOGIN_TEST) {
			loginTimestamp = null;
		}
		logout(authTokenService, router);
		return throwError(
			() => new Error('No authentication tokens available'),
		);
	}

	// *** TEMPORARY TEST CODE - Force expiration after 15 seconds ***
	const isTestExpired =
		ENABLE_RELOGIN_TEST &&
		loginTimestamp !== null &&
		now - loginTimestamp > RELOGIN_TEST_DELAY_MS;

	if (isTestExpired) {
		console.log('🧪 TEST: Forcing token expiration after 15 seconds');
	}
	// *** END TEST CODE ***

	// State 2: Token already expired (including test expiration)
	if (authTokenService.isTokenExpired() || isTestExpired) {
		if (isTestExpired) {
			console.log(
				'🧪 TEST MODE: Simulating token expiration - opening re-login modal',
			);
		} else {
			console.log('Token has naturally expired, opening re-login modal');
		}
		return openReLoginModal(modalService, http).pipe(
			switchMap((success) => {
				if (success) {
					// *** TEMPORARY TEST CODE - Reset timestamp after re-login ***
					if (ENABLE_RELOGIN_TEST) {
						loginTimestamp = Date.now();
						console.log(
							'🧪 TEST: Login timestamp reset after re-login',
						);
					}
					// *** END TEST CODE ***

					// After successful re-login, return the new token
					return of(authTokenService.getAccessToken());
				} else {
					return throwError(
						() => new Error('Re-authentication cancelled'),
					);
				}
			}),
			catchError((error) => {
				console.error('Re-authentication failed:', error);
				// If re-login fails, log out the user
				logout(authTokenService, router);
				return throwError(() => new Error('Re-authentication failed'));
			}),
		);
	}

	// State 3: Token will expire soon (within next refresh interval)
	if (authTokenService.isTokenExpiringSoon(refreshIntervalMillis)) {
		console.log('Token expiring soon, refreshing proactively');
		return authTokenService.refreshAccessToken().pipe(
			withRetry(MAX_RETRY_ATTEMPTS),
			tap(() => {
				// Start the timer after successful refresh
				authTokenService.startTokenRefreshTimer(
					environment.authTokenRefreshIntervalMinutes,
					true,
				);
			}),
			map((response) => response?.accessToken || authTokenService.getAccessToken()),
			catchError((error) => {
				console.error('Failed to refresh token:', error);
				// If refresh fails, try re-login modal
				return openReLoginModal(modalService, http).pipe(
					switchMap((success) => {
						if (success) {
							// *** TEMPORARY TEST CODE - Reset timestamp after re-login ***
							if (ENABLE_RELOGIN_TEST) {
								loginTimestamp = Date.now();
								console.log(
									'🧪 TEST: Login timestamp reset after re-login (from refresh failure)',
								);
							}
							// *** END TEST CODE ***

							return of(authTokenService.getAccessToken());
						} else {
							logout(authTokenService, router);
							return throwError(
								() => new Error('Re-authentication failed'),
							);
						}
					}),
				);
			}),
		);
	}

	// State 4: Token is valid and not expiring soon
	// Notify user activity to keep the session alive
	authTokenService.notifyUserActivity();

	return of(authTokenService.getAccessToken());
}

/**
 * Opens re-login modal for expired token authentication
 */
function openReLoginModal(
	modalService: NgbModal,
	http: HttpClient,
): Observable<boolean> {
	// Prevent multiple re-login modals from opening
	if (isReLoginModalOpen && reLoginPromise) {
		console.log('Re-login modal already open, reusing existing promise');
		return from(reLoginPromise);
	}

	// Clear tokens when opening re-login modal
	const authTokenService = inject(AuthTokenService);
	const router = inject(Router);
	authTokenService.clearTokens();
	console.log('Tokens cleared - re-login modal opening');

	// Mark modal as open
	isReLoginModalOpen = true;

	const modalRef = modalService.open(DynamicModalComponent, {
		backdrop: 'static',
		keyboard: false,
		centered: true,
	});

	// Configure the modal
	modalRef.componentInstance.title =
		'Istunto vanhentunut - Kirjaudu uudelleen';
	modalRef.componentInstance.component = LoginFormComponent;
	modalRef.componentInstance.showFooter = false;
	modalRef.componentInstance.componentInputs = {
		buttonText: 'Kirjaudu uudelleen',
		showLogo: false,
	};

	// Inject LoginService here to avoid circular dependency at module level
	const loginService = inject(LoginService);

	// Set up login form event handling
	modalRef.componentInstance.componentOutputs = {
		loginAttempt: (credentials: { username: string; password: string }) => {
			// Update the form state to show loading
			const loginFormInstance =
				modalRef.componentInstance.componentRef?.instance;
			if (loginFormInstance) {
				loginFormInstance.isLoggingIn = true;
				loginFormInstance.loginError = '';
			}

			// Attempt re-authentication (lightweight, doesn't refresh UI)
			loginService
				.ReAuthenticate(credentials.username, credentials.password)
				.subscribe({
					next: (success) => {
						if (loginFormInstance) {
							loginFormInstance.isLoggingIn = false;
						}

						if (success) {
							modalRef.close(true);
						} else {
							if (loginFormInstance) {
								loginFormInstance.loginError =
									'Kirjautuminen epäonnistui. Tarkista tunnuksesi.';
							}
						}
					},
					error: (error) => {
						if (loginFormInstance) {
							loginFormInstance.isLoggingIn = false;
							loginFormInstance.loginError =
								'Kirjautuminen epäonnistui: ' + error.message;
						}
					},
				});
		},
	};

	// Create a promise that we can reuse for multiple simultaneous requests
	reLoginPromise = modalRef.result
		.then(() => true)
		.catch((error) => {
			console.log('Re-login modal dismissed - logging out user', error);
			// User closed modal without re-authentication, log them out
			logout(authTokenService, router);
			throw new Error('Re-login cancelled by user');
		})
		.finally(() => {
			// Reset modal state when done
			isReLoginModalOpen = false;
			reLoginPromise = null;
		});

	return from(reLoginPromise);
}

/**
 * Handles 401 unauthorized responses
 */
function handleUnauthorized(
	req: HttpRequest<any>,
	next: HttpHandlerFn,
	authTokenService: AuthTokenService,
	modalService: NgbModal,
	router: Router,
	notificationService: NotificationService,
	http: HttpClient,
): Observable<HttpEvent<any>> {
	console.log(
		'🔄 Handling 401 unauthorized - clearing tokens and attempting re-authentication',
	);

	// Clear invalid token
	authTokenService.clearTokens();

	// Try to get a new token
	return ensureValidToken(
		authTokenService,
		modalService,
		router,
		notificationService,
		http,
		req,
		next,
	).pipe(
		switchMap((token) => {
			const authReq = req.clone({
				setHeaders: {
					Authorization: `Bearer ${token}`,
				},
			});
			return next(authReq).pipe(withRetry(MAX_RETRY_ATTEMPTS));
		}),
	);
}

/**
 * Logs out the user and navigates to login page
 */
function logout(authTokenService: AuthTokenService, router: Router): void {
	authTokenService.clearTokens();
	authTokenService.stopTokenRefreshTimer();

	// *** TEMPORARY TEST CODE - Reset timestamp on logout ***
	if (ENABLE_RELOGIN_TEST) {
		loginTimestamp = null;
		console.log('🧪 TEST: Login timestamp cleared on logout');
	}
	// *** END TEST CODE ***

	console.log('User logged out due to authentication failure');

	// Navigate to login page
	router.navigate(['/login']);
}

/**
 * Checks if a URL is an authentication endpoint that shouldn't be intercepted
 */
function isAuthEndpoint(url: string): boolean {
	return (
		url.includes('/login') ||
		url.includes('/refresh') ||
		url.includes('/register')
	);
}

/**
 * Determines if a request needs authentication
 */
function needsAuthentication(req: HttpRequest<any>): boolean {
	// Add authentication to all API requests except public ones
	const publicEndpoints = ['/login', '/register', '/refresh'];
	return !publicEndpoints.some((endpoint) => req.url.includes(endpoint));
}

/**
 * Determines if a request should be allowed to proceed during login process
 */
function shouldAllowDuringLogin(req: HttpRequest<any>): boolean {
	// Allow static assets and public resources during login
	return (
		req.url.includes('/assets/') ||
		req.url.includes('.png') ||
		req.url.includes('.jpg') ||
		req.url.includes('.jpeg') ||
		req.url.includes('.svg') ||
		req.url.includes('.css') ||
		req.url.includes('.js') ||
		req.url.includes('.ico') ||
		req.url.includes('.woff') ||
		req.url.includes('.ttf') ||
		isAuthEndpoint(req.url) // Allow auth endpoints during login
	);
}
